import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Autocomplete,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stack
} from '@mui/material';
import {
  RestartAlt as UnsignIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Assignment as FormsIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../../config/api';

// Unsign Employee Forms (Glen, November 4, 2026). Admin and Super Admin
// only (TKT-576). They see every employee's signed forms in one list and can
// unsign one or many. Unsigning puts the form back to "to sign" for the
// employee; the signed copy is archived (kept in HR > Employee Forms
// history), not deleted.
const UnsignEmployeeForms = () => {
  const { t, i18n } = useTranslation();
  const isSpanish = (i18n.language || 'en').toLowerCase().startsWith('es');

  const [rows, setRows] = useState([]);
  const [formTypes, setFormTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState(null);
  const [formTypeFilter, setFormTypeFilter] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [confirmIds, setConfirmIds] = useState(null);
  const [unsigning, setUnsigning] = useState(false);

  const fetchSigned = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await makeApiCall('/api/employee-forms/admin/signed');
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('unsignForms.errors.load'));
      setRows(data.forms || []);
      setFormTypes(data.form_types || []);
      setSelected(new Set());
    } catch (err) {
      console.error('Error loading signed forms:', err);
      setError(err.message || t('unsignForms.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchSigned();
  }, [fetchSigned]);

  const formName = useCallback(
    (row) => (isSpanish ? row.form_name_es : row.form_name_en) || row.form_name_en,
    [isSpanish]
  );

  const formatDate = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleString(i18n.language || undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const employees = useMemo(() => {
    const byId = new Map();
    rows.forEach((row) => {
      if (!byId.has(row.user_id)) {
        byId.set(row.user_id, { user_id: row.user_id, name: row.employee_name, username: row.username });
      }
    });
    return [...byId.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [rows]);

  const visibleRows = useMemo(() => rows.filter((row) => (
    (!employeeFilter || row.user_id === employeeFilter.user_id)
    && (!formTypeFilter || row.form_type_id === Number(formTypeFilter))
  )), [rows, employeeFilter, formTypeFilter]);

  // Keep only selections that are still on screen after a filter change.
  useEffect(() => {
    setSelected((prev) => {
      const visibleIds = new Set(visibleRows.map((row) => row.id));
      const next = new Set([...prev].filter((id) => visibleIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [visibleRows]);

  const allVisibleSelected = visibleRows.length > 0 && visibleRows.every((row) => selected.has(row.id));
  const someVisibleSelected = visibleRows.some((row) => selected.has(row.id));

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelected(allVisibleSelected ? new Set() : new Set(visibleRows.map((row) => row.id)));
  };

  const handleDownload = async (row) => {
    setError('');
    try {
      const response = await makeApiCall(`/api/employee-forms/${row.id}/pdf`);
      if (!response.ok) throw new Error(t('unsignForms.errors.download'));
      const blob = await response.blob();
      const base = `${row.employee_name || 'employee'}-${formName(row) || 'signed-form'}`;
      const safeTitle = base.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'signed-form';
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${safeTitle}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading signed form:', err);
      setError(err.message || t('unsignForms.errors.download'));
    }
  };

  const confirmRows = useMemo(
    () => (confirmIds ? rows.filter((row) => confirmIds.includes(row.id)) : []),
    [confirmIds, rows]
  );

  const handleConfirmUnsign = async () => {
    if (!confirmIds?.length) return;
    setUnsigning(true);
    setError('');
    setSuccess('');
    try {
      const response = await makeApiCall('/api/employee-forms/admin/unsign', {
        method: 'POST',
        body: JSON.stringify({ submission_ids: confirmIds })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('unsignForms.errors.unsign'));
      setSuccess(t('unsignForms.unsignSuccess', { count: data.unsigned }));
      setConfirmIds(null);
      await fetchSigned();
    } catch (err) {
      console.error('Error unsigning forms:', err);
      setError(err.message || t('unsignForms.errors.unsign'));
    } finally {
      setUnsigning(false);
    }
  };

  const confirmSingle = confirmRows.length === 1 ? confirmRows[0] : null;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <FormsIcon sx={{ fontSize: 32, color: '#c62828' }} />
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#000062', fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
          {t('unsignForms.title')}
        </Typography>
      </Box>
      <Typography variant="body1" sx={{ color: '#666', mb: 3, maxWidth: 820 }}>
        {t('unsignForms.description')}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <Autocomplete
            options={employees}
            value={employeeFilter}
            onChange={(e, value) => setEmployeeFilter(value)}
            getOptionLabel={(option) => (option.username ? `${option.name} (${option.username})` : option.name)}
            isOptionEqualToValue={(option, value) => option.user_id === value.user_id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('unsignForms.employee')}
                placeholder={t('unsignForms.allEmployees')}
                inputProps={{ ...params.inputProps, autoComplete: 'off', 'data-lpignore': 'true', 'data-1p-ignore': 'true', 'data-form-type': 'other' }}
              />
            )}
            sx={{ minWidth: 280, flex: 1 }}
          />
          <TextField
            select
            label={t('unsignForms.form')}
            value={formTypeFilter}
            onChange={(e) => setFormTypeFilter(e.target.value)}
            sx={{ minWidth: 260, flex: 1 }}
          >
            <MenuItem value="">{t('unsignForms.allForms')}</MenuItem>
            {formTypes.map((ft) => (
              <MenuItem key={ft.id} value={String(ft.id)}>{formName(ft)}</MenuItem>
            ))}
          </TextField>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchSigned}
            disabled={loading}
          >
            {t('unsignForms.refresh')}
          </Button>
        </Stack>
      </Paper>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ sm: 'center' }} justifyContent="space-between">
        <Chip label={t('unsignForms.shownCount', { count: visibleRows.length })} />
        <Button
          variant="contained"
          color="error"
          startIcon={<UnsignIcon />}
          disabled={selected.size === 0}
          onClick={() => setConfirmIds([...selected])}
        >
          {t('unsignForms.unsignSelected', { count: selected.size })}
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label={t('common.loading')} />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small" aria-label={t('unsignForms.tableLabel')}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allVisibleSelected}
                      indeterminate={someVisibleSelected && !allVisibleSelected}
                      onChange={toggleAllVisible}
                      disabled={visibleRows.length === 0}
                      inputProps={{ 'aria-label': t('unsignForms.selectAll') }}
                    />
                  </TableCell>
                  <TableCell><strong>{t('unsignForms.employee')}</strong></TableCell>
                  <TableCell><strong>{t('unsignForms.form')}</strong></TableCell>
                  <TableCell><strong>{t('unsignForms.language')}</strong></TableCell>
                  <TableCell><strong>{t('unsignForms.signed')}</strong></TableCell>
                  <TableCell align="right"><strong>{t('unsignForms.actions')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#666' }}>
                      {t('unsignForms.empty')}
                    </TableCell>
                  </TableRow>
                )}
                {visibleRows.map((row) => (
                  <TableRow key={row.id} hover selected={selected.has(row.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.has(row.id)}
                        onChange={() => toggleRow(row.id)}
                        inputProps={{ 'aria-label': `${row.employee_name} ${formName(row)}` }}
                      />
                    </TableCell>
                    <TableCell>
                      {row.employee_name}
                      {row.user_is_active === false && (
                        <Chip size="small" variant="outlined" label={t('unsignForms.inactive')} sx={{ ml: 1 }} />
                      )}
                    </TableCell>
                    <TableCell>{formName(row)}</TableCell>
                    <TableCell>{row.language === 'es' ? t('unsignForms.spanish') : t('unsignForms.english')}</TableCell>
                    <TableCell>{formatDate(row.submitted_at)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownload(row)}
                        >
                          {t('unsignForms.download')}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<UnsignIcon />}
                          onClick={() => setConfirmIds([row.id])}
                        >
                          {t('unsignForms.unsign')}
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog
        open={Boolean(confirmIds)}
        onClose={() => !unsigning && setConfirmIds(null)}
        aria-labelledby="unsign-forms-dialog-title"
      >
        <DialogTitle id="unsign-forms-dialog-title">
          {t('unsignForms.confirmTitle', { count: confirmRows.length })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmSingle
              ? t('unsignForms.confirmBodySingle', { form: formName(confirmSingle), employee: confirmSingle.employee_name })
              : t('unsignForms.confirmBodyMany', { count: confirmRows.length })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmIds(null)} disabled={unsigning}>
            {t('common.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmUnsign}
            disabled={unsigning || confirmRows.length === 0}
            startIcon={unsigning ? <CircularProgress size={18} color="inherit" /> : <UnsignIcon />}
          >
            {t('unsignForms.confirmButton', { count: confirmRows.length })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UnsignEmployeeForms;
